/**
 * 配對分析 API 模組
 * 處理雙人命盤配對分析相關的 API 請求
 */

import apiClient from './client';

/**
 * 配對分析請求參數
 */
export interface PairRequest {
  person_a: {
    birth_time: string;      // YYYYMMDDhh
    gender: 'Male' | 'Female';
    name?: string;           // 可選，顯示用
  };
  person_b: {
    birth_time: string;
    gender: 'Male' | 'Female';
    name?: string;
  };
  question_id: string;       // PAIR001-PAIR034
  share_token?: string;      // 從分享連結觸發時傳入，後端免費處理
}

/**
 * 配對分析響應
 */
export interface PairResponse {
  success: boolean;
  output?: string;           // 完整分析文字
  result?: {
    compatibility_score?: number;      // 綜合配對度 0-100
    personality_match?: number;        // 個性契合度 0-100
    communication_match?: number;      // 溝通協調度 0-100
    values_match?: number;             // 價值觀一致性 0-100
    harmony_score?: number;            // 相處融洽度 0-100
    stability_score?: number;          // 長期穩定性 0-100
    strengths?: string[];              // 優勢特質
    weaknesses?: string[];             // 需注意事項
    suggestions?: string[];            // 互補建議
    [key: string]: any;                // 其他結構化資料
  };
  question_text?: string;
  question_id?: string;
  category_id?: string;
  cost?: number;
  balance_after?: number;
  balance_before?: number;
  query_id?: number;
  created_at?: string;
  person_a_info?: {
    birth_time: string;
    gender: string;
    name?: string;
  };
  person_b_info?: {
    birth_time: string;
    gender: string;
    name?: string;
  };
  message?: string;
  error?: string;
}

/**
 * 配對分析歷史響應
 */
export interface PairHistoryResponse {
  success: boolean;
  count: number;
  total: number;
  queries: Array<{
    query_id: number;
    question_id: string;
    question_text: string;
    person_a_birth_time: string;
    person_a_gender: string;
    person_a_name?: string;
    person_b_birth_time: string;
    person_b_gender: string;
    person_b_name?: string;
    cost: number;
    created_at: string;
  }>;
}

/**
 * 配對分析類型
 */
export type RelationshipType = 'romantic' | 'marriage' | 'friendship' | 'business';

/**
 * 關係類型選項
 */
export const relationshipTypeOptions = [
  { value: 'romantic' as RelationshipType, label: '戀愛配對', description: '適合分析戀愛關係的契合度' },
  { value: 'marriage' as RelationshipType, label: '婚姻配對', description: '適合分析婚姻關係的長期穩定性' },
  { value: 'friendship' as RelationshipType, label: '友誼配對', description: '適合分析朋友關係的相處模式' },
  { value: 'business' as RelationshipType, label: '工作夥伴', description: '適合分析工作合作的協調性' },
];

/**
 * 多人配對分析請求參數
 */
export interface MultiPairRequest {
  person_a: {
    birth_time: string;      // YYYYMMDDhh
    gender: 'Male' | 'Female';
    name?: string;           // 可選，顯示用
  };
  candidates: Array<{
    birth_time: string;
    gender: 'Male' | 'Female';
    name?: string;
  }>;
  question_id: string;       // PAIR016等
}

/**
 * 多人配對分析響應
 */
export interface MultiPairResponse {
  success: boolean;
  results?: Array<{
    candidate_name: string;
    output: string;
    result: any;
  }>;
  question_id?: string;
  question_text?: string;
  category_id?: string;
  cost?: number;
  balance_after?: number;
  balance_before?: number;
  query_id?: number;
  person_a_info?: {
    birth_time: string;
    gender: string;
    name?: string;
  };
  candidates_count?: number;
  created_at?: string;
  message?: string;
  error?: string;
}

/**
 * 執行配對分析
 * @param request 配對分析請求參數
 * @returns 配對分析結果
 */
export const analyzePair = async (request: PairRequest): Promise<PairResponse> => {
  const response = await apiClient.post<PairResponse>('/api/pair/analyze', request);
  return response as any as PairResponse;
};

/**
 * 執行多人配對分析（只扣一次款）
 * @param request 多人配對分析請求參數
 * @returns 多人配對分析結果
 */
export const analyzeMultiPair = async (request: MultiPairRequest): Promise<MultiPairResponse> => {
  const response = await apiClient.post<MultiPairResponse>('/api/pair/analyze-multi', request);
  return response as any as MultiPairResponse;
};

/**
 * 取得配對分析歷史記錄
 * @param limit 每頁筆數（預設 20）
 * @param offset 偏移量（預設 0）
 * @returns 配對分析歷史
 */
export const getPairHistory = async (
  limit: number = 20,
  offset: number = 0
): Promise<PairHistoryResponse> => {
  const response = await apiClient.get<PairHistoryResponse>('/api/pair/history', {
    params: { limit, offset }
  });
  return response as any as PairHistoryResponse;
};

/**
 * 取得特定配對分析詳情
 * @param queryId 查詢 ID
 * @returns 配對分析詳情
 */
export const getPairDetail = async (queryId: number): Promise<PairResponse> => {
  const response = await apiClient.get<PairResponse>(`/api/pair/history/${queryId}`);
  return response as any as PairResponse;
};

/**
 * 驗證出生時間格式（YYYYMMDDhh）
 * @param birthTime 出生時間字串
 * @returns 是否有效
 */
export const validatePairBirthTime = (birthTime: string): boolean => {
  // 必須是 10 位數字
  if (!/^\d{10}$/.test(birthTime)) {
    return false;
  }

  const year = parseInt(birthTime.substring(0, 4));
  const month = parseInt(birthTime.substring(4, 6));
  const day = parseInt(birthTime.substring(6, 8));
  const hour = parseInt(birthTime.substring(8, 10));

  // 年份範圍：1900-2100
  if (year < 1900 || year > 2100) {
    return false;
  }

  // 月份範圍：1-12
  if (month < 1 || month > 12) {
    return false;
  }

  // 日期範圍：1-31
  if (day < 1 || day > 31) {
    return false;
  }

  // 時辰必須是單數：01, 03, 05, 07, 09, 11, 13, 15, 17, 19, 21, 23
  const validHours = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23];
  if (!validHours.includes(hour)) {
    return false;
  }

  return true;
};

/**
 * 計算配對評分的顏色
 * @param score 評分（0-100）
 * @returns 顏色代碼
 */
export const getScoreColor = (score: number): string => {
  if (score >= 80) return '#4caf50'; // 綠色 - 優
  if (score >= 60) return '#8bc34a'; // 淺綠 - 良
  if (score >= 40) return '#ff9800'; // 橘色 - 中
  if (score >= 20) return '#f44336'; // 紅色 - 差
  return '#d32f2f';                  // 深紅 - 極差
};

/**
 * 取得評分等級
 * @param score 評分（0-100）
 * @returns 等級文字
 */
export const getScoreGrade = (score: number): string => {
  if (score >= 80) return '優';
  if (score >= 60) return '良';
  if (score >= 40) return '中';
  if (score >= 20) return '差';
  return '極差';
};

/**
 * 格式化配對分析結果摘要
 * @param result 配對結果
 * @returns 格式化的摘要文字
 */
export const formatPairSummary = (result: PairResponse['result']): string => {
  if (!result) return '';

  const lines: string[] = [];

  if (result.compatibility_score !== undefined) {
    lines.push(`綜合配對度：${result.compatibility_score} 分（${getScoreGrade(result.compatibility_score)}）`);
  }

  if (result.personality_match !== undefined) {
    lines.push(`個性契合度：${result.personality_match} 分`);
  }

  if (result.communication_match !== undefined) {
    lines.push(`溝通協調度：${result.communication_match} 分`);
  }

  if (result.values_match !== undefined) {
    lines.push(`價值觀一致性：${result.values_match} 分`);
  }

  return lines.join('\n');
};

/**
 * PAIR 類別問題是否需要雙人資料
 * PAIR001-PAIR025: 需要雙人資料
 * PAIR026-PAIR034: 需要多人資料（暫不支援）
 * @param questionId 問題 ID
 * @returns 是否需要雙人資料
 */
export const isPairQuestionSupported = (questionId: string): boolean => {
  const num = parseInt(questionId.replace('PAIR', ''));
  return num >= 1 && num <= 25;
};

/**
 * 預設匯出
 */
const pairApi = {
  analyzePair,
  getPairHistory,
  getPairDetail,
  validatePairBirthTime,
  getScoreColor,
  getScoreGrade,
  formatPairSummary,
  isPairQuestionSupported,
  relationshipTypeOptions,
};

export default pairApi;
